import React from 'react'
import {
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import type { DisplaySummaryRow } from '../../../../utils/projectOverviewSummary'
import type { OverviewHoursPieSlice } from '../../../../utils/projectOverviewCharts'
import { overviewRechartsPalette } from './overview-recharts-palette'

export interface OverviewHoursMixPieChartProps {
  isDark: boolean
  displaySummary: DisplaySummaryRow
  overviewHoursPieData: OverviewHoursPieSlice[]
}

export function OverviewHoursMixPieChart({
  isDark,
  displaySummary,
  overviewHoursPieData,
}: OverviewHoursMixPieChartProps) {
  const { tooltipBg, tooltipBorder } = overviewRechartsPalette(isDark)
  const hasHours =
    overviewHoursPieData.length > 0 &&
    (Number(displaySummary.billable_hours) || 0) +
      (Number(displaySummary.non_billable_hours) || 0) >
      0

  return (
    <div className="rounded-xl shadow-md border border-slate-200 bg-white p-4 min-h-[280px]">
      <h3 className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
        Hours mix
      </h3>
      {hasHours ? (
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={overviewHoursPieData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={95}
              paddingAngle={3}
              stroke="#e5e7eb"
              strokeWidth={1.5}
              dataKey="value"
              nameKey="name"
            >
              {overviewHoursPieData.map((entry, i) => (
                <Cell
                  key={i}
                  fill={entry.color}
                  stroke={entry.color}
                  strokeWidth={1}
                />
              ))}
              <LabelList
                dataKey="value"
                position="inside"
                formatter={(value: number) => {
                  const total = overviewHoursPieData.reduce(
                    (s, d) => s + (d.value || 0),
                    0,
                  )
                  if (total <= 0) return ''
                  const pct = ((Number(value) / total) * 100).toFixed(0)
                  const hrs = Number(value).toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                  })
                  return `${pct}% (${hrs})`
                }}
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  fill: '#ffffff',
                  stroke: 'rgba(15,23,42,0.4)',
                  strokeWidth: 1.5,
                }}
              />
            </Pie>
            <Tooltip
              formatter={(v: number) => [
                `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })} hrs`,
                '',
              ]}
              contentStyle={{
                fontSize: 12,
                backgroundColor: tooltipBg,
                border: `1px solid ${tooltipBorder}`,
                borderRadius: 8,
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: 12 }}
              formatter={(value, entry) => {
                const total = overviewHoursPieData.reduce(
                  (s, d) => s + (d.value || 0),
                  0,
                )
                const payload = entry.payload as { value?: number } | undefined
                const pct =
                  total > 0 && payload?.value != null
                    ? ((payload.value / total) * 100).toFixed(0)
                    : '0'
                return `${value} (${pct}%)`
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[260px] flex items-center justify-center text-xs text-slate-500">
          No hours data
        </div>
      )}
    </div>
  )
}
